import { useState, useRef, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import { getClassNames, retrieveFromLocalStorage } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import Message from '../../../../common/message/Message';

import { ReactComponent as SendIcon } from '../../../../../resources/icons/send.svg';

import './deal-communication.scss';

const DealCommunication = ({
  chats,
  sendMessage,
  admin,
  assistanceRequired,
  resolveAssistance,
  messageSentOnSuccess,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const endOfMessagesRef = useRef(null);
  const inputRef = useRef(null);
  const messageContainerRef = useRef(null);
  const numberOfMessagesRef = useRef(0);
  const atBottomRef = useRef(true);

  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView();
      atBottomRef.current = true;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!chats || chats.length === 0) {
      return;
    }

    const numberOfMessages = chats[activeTab].messages.length;
    const isNewMessages = numberOfMessages > numberOfMessagesRef.current;

    if (isNewMessages) {
      numberOfMessagesRef.current = numberOfMessages;
    }

    if (isNewMessages && atBottomRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chats, activeTab]);

  const handleTabClick = (index) => {
    const numberOfMessages = chats[index].messages.length;
    numberOfMessagesRef.current = numberOfMessages;
    setActiveTab(index);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    inputRef.current.style.height = '40px';
    inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;

    if (inputRef.current.scrollHeight <= 40) {
      messageContainerRef.current.style.marginBottom = '40px';
      messageContainerRef.current.style.maxHeight = `calc(100vh - ${admin ? 245 : 390}px)`;
    } else if (inputRef.current.scrollHeight <= 60) {
      messageContainerRef.current.style.marginBottom = '66px';
      messageContainerRef.current.style.maxHeight = `calc(100vh - ${admin ? 271 : 416}px)`;
    } else if (inputRef.current.scrollHeight <= 82) {
      messageContainerRef.current.style.marginBottom = '90px';
      messageContainerRef.current.style.maxHeight = `calc(100vh - ${admin ? 295 : 440}px)`;
    }

    if (atBottomRef.current) {
      endOfMessagesRef.current.scrollIntoView();
    }
  };

  const handleSendMessageClick = () => {
    if (!message || sendingMessage) return;

    setSendingMessage(true);

    const activeChat = chats[activeTab];
    sendMessage(activeChat.externalReference, message, () => {
      inputRef.current.style.height = '40px';
      messageContainerRef.current.style.marginBottom = '40px';
      messageContainerRef.current.style.maxHeight = `calc(100vh - ${admin ? 245 : 390}px)`;
      atBottomRef.current = true;
      setMessage('');
      setSendingMessage(false);
      messageOnSuccessFunction(activeChat);
    });
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
    const atBottom = scrollTop + clientHeight >= scrollHeight;

    if (atBottom !== atBottomRef.current) {
      atBottomRef.current = atBottom;
    }
  };

  const messageOnSuccessFunction = (activeChat) => {
    if (!activeChat) return;

    if (activeChat.type === 'General') {
      return;
    }

    messageSentOnSuccess();
  };

  const getHelpMessageText = (activeChat) => {
    if (!activeChat) return;

    if (activeChat.type === 'Buyer' || activeChat.type === 'Seller') {
      return 'Please click the resolve button if you are happy that your problem has been resolved';
    }

    if (activeChat.type === 'ATP Facilitator') {
      return 'I need more help';
    }
  };

  const sendHelpMessage = () => {
    if (sendingMessage) return;

    setSendingMessage(true);

    const activeChat = chats[activeTab];

    sendMessage(activeChat.externalReference, getHelpMessageText(activeChat), () => {
      inputRef.current.style.height = '40px';
      messageContainerRef.current.style.marginBottom = '40px';
      messageContainerRef.current.style.maxHeight = `calc(100vh - ${admin ? 245 : 390}px)`;
      atBottomRef.current = true;
      setSendingMessage(false);
    });
  };

  const getHelpButtonText = (activeChat) => {
    if (!activeChat) return;

    if (activeChat.type === 'Buyer' || activeChat.type === 'Seller') {
      return 'PROMPT USER TO CLOSE REQUEST';
    }

    if (activeChat.type === 'ATP Facilitator') {
      return 'I NEED MORE HELP';
    }
  };

  const getResolveButtonText = (activeChat) => {
    if (!activeChat) return;

    if (activeChat.type === 'Buyer' || activeChat.type === 'Seller') {
      return 'CLOSE REQUEST';
    }

    if (activeChat.type === 'ATP Facilitator') {
      return 'THANKS, MY PROBLEM HAS BEEN RESOLVED';
    }
  };

  const userResolveAssistanceButtons = (index, reversedMessages, personExternalReference, activeChat) => {
    if (!assistanceRequired) return;
    if (activeChat.type === 'General') return;

    let indexButtonMessage = 0;
    reversedMessages.forEach((message, index) => {
      if (message.personExternalReference !== personExternalReference) {
        indexButtonMessage = index;
      }
    });

    if (indexButtonMessage === 0 && reversedMessages[0].personExternalReference === personExternalReference) {
      return;
    }

    if (index === indexButtonMessage) {
      return (
        <div className="deal-communication__assistance-buttons">
          <Button onClick={resolveAssistance}> {getResolveButtonText(activeChat)}</Button>
          <Button onClick={sendHelpMessage}>{getHelpButtonText(activeChat)} </Button>
        </div>
      );
    }
  };

  const renderMessages = () => {
    const activeChat = chats[activeTab];

    if (!activeChat || !activeChat.messages || activeChat.messages.length === 0) {
      return (
        <li className="deal-communication__chat__no-messages">
          <p>No Messages</p>
        </li>
      );
    }

    const personExternalReference = retrieveFromLocalStorage('personExternalReference');

    const reversedMessages = [...activeChat.messages].reverse();

    return reversedMessages.map((message, index) => {
      const right = message.personExternalReference === personExternalReference;

      return (
        <Fragment key={index}>
          <Message message={message} right={right} showSenderName={!right} />
          {userResolveAssistanceButtons(index, reversedMessages, personExternalReference, activeChat)}
        </Fragment>
      );
    });
  };

  return (
    <section className="deal-communication">
      <h2>Chat</h2>
      <div className="deal-communication__tabs">
        {chats.map((chat, index) => (
          <button
            key={index}
            className={getClassNames('deal-communication__tab', { active: index === activeTab })}
            onClick={() => handleTabClick(index)}
          >
            <span>{admin && chat.organisation ? `${chat.type}: ${chat.organisation}` : chat.type}</span>
          </button>
        ))}
      </div>
      <section className={getClassNames('deal-communication__chat', { 'first-tab-active': activeTab === 0 })}>
        <ul
          className={getClassNames('deal-communication__chat__messages', { long: admin })}
          ref={messageContainerRef}
          onScroll={handleScroll}
        >
          {renderMessages()}
          <span ref={endOfMessagesRef} />
        </ul>
        <div className="deal-communication__chat__input">
          <textarea value={message} onChange={handleMessageChange} ref={inputRef} />
          <SendIcon
            className={getClassNames('deal-communication__chat__input__button', { sending: sendingMessage })}
            onClick={handleSendMessageClick}
          />
        </div>
      </section>
    </section>
  );
};

DealCommunication.propTypes = {
  chats: PropTypes.arrayOf(PropTypes.shape({})),
  sendMessage: PropTypes.func.isRequired,
  admin: PropTypes.bool,
  assistanceRequired: PropTypes.bool,
  resolveAssistance: PropTypes.func.isRequired,
  messageSentOnSuccess: PropTypes.func.isRequired,
};

DealCommunication.defaultProps = {
  chats: [],
  admin: false,
  assistanceRequired: false,
};

export default DealCommunication;
